/**
 * /services/submit — request form for a memorial service. Captures loved-one
 * details, memorial site info, requester contact info, and preferences.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Heart, MapPin, User, Calendar, Camera, X } from "lucide-react";

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

const DEFAULT = {
  title: "", short_description: "", detailed_description: "",
  loved_one_full_name: "", loved_one_family_name: "",
  loved_one_year_of_birth: "", loved_one_year_of_death: "",
  loved_one_native_place: "", loved_one_current_address: "",
  siblings: "", relationship_to_loved_one: "",
  memorial_type: "", memorial_address: "", memorial_country: "", memorial_state: "",
  memorial_city: "", memorial_gps_coords: "", memorial_notes: "",
  preferred_date: "", flexible_dates: true, recurring: false, recurring_pattern: "",
  requester_full_name: "", requester_email: "", requester_phone: "",
  requester_country: "", requester_city: "",
  preferred_contact: "email",
  budget_range: "", offering: "",
  is_public: true, consent: false,
};

export default function ServiceSubmit() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [taxonomy, setTaxonomy] = useState({});
  const [category, setCategory] = useState("");
  const [subcategory, setSubcategory] = useState("");
  const [f, setF] = useState(DEFAULT);
  const [cover, setCover] = useState("");
  const [images, setImages] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    http.get("/services/config").then((r) => setTaxonomy(r.data.taxonomy || {})).catch(() => {});
  }, []);

  // Pre-fill requester info from profile once we know the user.
  useEffect(() => {
    if (user) {
      setF((prev) => ({
        ...prev,
        requester_full_name: prev.requester_full_name || user.name || "",
        requester_email: prev.requester_email || user.email || "",
        requester_country: prev.requester_country || user.country || "",
        requester_city: prev.requester_city || user.city || "",
      }));
    }
  }, [user]);

  if (loading) return <div className="p-16 text-center">Loading…</div>;
  if (!user) {
    return (
      <div className="max-w-3xl mx-auto p-16 text-center">
        <h1 className="font-display text-3xl">Please sign in to request a service.</h1>
        <button onClick={() => nav("/login?next=/services/submit")} className="wofy-btn-primary mt-6">
          Sign in
        </button>
      </div>
    );
  }

  const upd = (k, v) => setF((prev) => ({ ...prev, [k]: v }));
  const subs = taxonomy[category] || [];

  const onCover = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 4 * 1024 * 1024) return toast.error("Cover image must be < 4 MB");
    setCover(await fileToDataUrl(file));
  };
  const onAdd = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (images.length >= 6) return toast.error("Max 6 images");
    if (file.size > 3 * 1024 * 1024) return toast.error("Each image < 3 MB");
    const dataUrl = await fileToDataUrl(file);
    setImages((p) => [...p, dataUrl]);
    e.target.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!category) return toast.error("Please pick a category");
    if (!f.title.trim() || f.title.length < 6) return toast.error("Title needs at least 6 characters");
    if (!f.short_description.trim() || f.short_description.length < 10)
      return toast.error("Short description needs at least 10 characters");
    if (!f.requester_full_name.trim()) return toast.error("Your name is required");
    if (!f.requester_email.trim()) return toast.error("Contact email is required");
    if (!f.consent) return toast.error("Please confirm the details and consent to publish");

    const totalBytes = (cover?.length || 0) + images.reduce((s, i) => s + (i?.length || 0), 0);
    if (totalBytes > 10 * 1024 * 1024) {
      return toast.error("Photos exceed 10 MB total — please compress or remove some.");
    }

    setSubmitting(true);
    try {
      const body = {
        ...f,
        category, subcategory: subcategory || null,
        cover_image: cover || null, images,
      };
      const { data } = await http.post("/services", body);
      toast.success(data.status === "approved" ? "Published!" : "Submitted for review — thank you.");
      nav(`/services/${data.slug}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submit failed");
    } finally {
      setSubmitting(false);
    }
  };

  const inp = "w-full px-3 py-2 rounded-lg border bg-transparent text-sm";
  const st = { borderColor: "var(--wofy-border)" };

  return (
    <div data-testid="services-submit-page" className="max-w-[900px] mx-auto px-6 lg:px-10 py-14">
      <div className="overline mb-3">CELEBRATE A LIFE · REQUEST A SERVICE</div>
      <h1 className="font-display text-4xl md:text-5xl leading-tight">
        Ask a trusted local for help.
      </h1>
      <p className="mt-3 text-sm max-w-2xl" style={{ color: "var(--wofy-text-soft)" }}>
        Every field marked * is required. Contact details stay private — visitors
        must sign in to see your email or phone. Verified contributors publish
        instantly; everyone else lands in the moderation queue.
      </p>

      <form onSubmit={submit} className="mt-8 space-y-6">
        {/* Category */}
        <section className="wofy-card space-y-4">
          <div className="overline">SERVICE CATEGORY</div>
          <div className="grid md:grid-cols-2 gap-4">
            <select
              data-testid="ss-category" required value={category}
              onChange={(e) => { setCategory(e.target.value); setSubcategory(""); }}
              className={inp} style={st}
            >
              <option value="">Select category *</option>
              {Object.keys(taxonomy).map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select
              data-testid="ss-subcategory" value={subcategory}
              onChange={(e) => setSubcategory(e.target.value)}
              disabled={!subs.length}
              className={inp} style={st}
            >
              <option value="">Subcategory (optional)</option>
              {subs.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </section>

        {/* Basics */}
        <section className="wofy-card space-y-4">
          <div className="overline">REQUEST DETAILS</div>
          <input data-testid="ss-title" required minLength={6} placeholder="Title of the request *"
                 value={f.title} onChange={(e) => upd("title", e.target.value)} className={inp} style={st} />
          <input data-testid="ss-short" required placeholder="Short description (1–2 sentences) *"
                 value={f.short_description} onChange={(e) => upd("short_description", e.target.value)}
                 maxLength={500} className={inp} style={st} />
          <textarea
            data-testid="ss-detailed"
            rows={4}
            placeholder="Any extra context — dates, cultural preferences, favorite songs, prayers, etc."
            value={f.detailed_description}
            onChange={(e) => upd("detailed_description", e.target.value)}
            maxLength={8000} className={inp} style={st}
          />
        </section>

        {/* Loved one */}
        <section className="wofy-card space-y-4">
          <div className="overline inline-flex items-center gap-1.5">
            <Heart className="w-3.5 h-3.5" style={{ color: "var(--wofy-primary)" }} />
            ABOUT THE LOVED ONE
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <input placeholder="Full name" data-testid="ss-loved-full-name"
                   value={f.loved_one_full_name} onChange={(e) => upd("loved_one_full_name", e.target.value)}
                   className={inp} style={st} />
            <input placeholder="Family / surname" data-testid="ss-loved-family"
                   value={f.loved_one_family_name} onChange={(e) => upd("loved_one_family_name", e.target.value)}
                   className={inp} style={st} />
            <input placeholder="Year of birth (e.g. 1935)" data-testid="ss-loved-birth"
                   value={f.loved_one_year_of_birth} onChange={(e) => upd("loved_one_year_of_birth", e.target.value)}
                   className={inp} style={st} />
            <input placeholder="Year of passing (if applicable)"
                   value={f.loved_one_year_of_death} onChange={(e) => upd("loved_one_year_of_death", e.target.value)}
                   className={inp} style={st} />
            <input placeholder="Native place / hometown" data-testid="ss-loved-native"
                   value={f.loved_one_native_place} onChange={(e) => upd("loved_one_native_place", e.target.value)}
                   className={inp} style={st} />
            <input placeholder="Current address (if visiting a living relative)"
                   value={f.loved_one_current_address} onChange={(e) => upd("loved_one_current_address", e.target.value)}
                   className={inp} style={st} />
            <input placeholder="Siblings' names (comma-separated)" data-testid="ss-siblings"
                   value={f.siblings} onChange={(e) => upd("siblings", e.target.value)}
                   className={`${inp} md:col-span-2`} style={st} />
            <input placeholder="Your relationship to them (e.g. son, granddaughter)"
                   value={f.relationship_to_loved_one} onChange={(e) => upd("relationship_to_loved_one", e.target.value)}
                   className={`${inp} md:col-span-2`} style={st} />
          </div>
        </section>

        {/* Memorial site */}
        <section className="wofy-card space-y-4">
          <div className="overline inline-flex items-center gap-1.5">
            <MapPin className="w-3.5 h-3.5" style={{ color: "var(--wofy-primary)" }} />
            MEMORIAL / SITE LOCATION
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <select value={f.memorial_type} onChange={(e) => upd("memorial_type", e.target.value)}
                    className={inp} style={st} data-testid="ss-mem-type">
              <option value="">Site type</option>
              <option value="Grave / cemetery">Grave / cemetery</option>
              <option value="Memorial / cenotaph">Memorial / cenotaph</option>
              <option value="Family home">Family home</option>
              <option value="Church / temple / mosque / synagogue">Church / temple / mosque / synagogue</option>
              <option value="Public place">Public place (park, beach, monument)</option>
              <option value="Elderly relative's home">Elderly relative's home</option>
              <option value="Other">Other</option>
            </select>
            <input placeholder="GPS coords (optional, lat,lng)"
                   value={f.memorial_gps_coords} onChange={(e) => upd("memorial_gps_coords", e.target.value)}
                   className={inp} style={st} />
            <input placeholder="City / town" data-testid="ss-mem-city"
                   value={f.memorial_city} onChange={(e) => upd("memorial_city", e.target.value)}
                   className={inp} style={st} />
            <input placeholder="State / province"
                   value={f.memorial_state} onChange={(e) => upd("memorial_state", e.target.value)}
                   className={inp} style={st} />
            <input placeholder="Country" data-testid="ss-mem-country"
                   value={f.memorial_country} onChange={(e) => upd("memorial_country", e.target.value)}
                   className={inp} style={st} />
            <input placeholder="Full address (kept private)" data-testid="ss-mem-address"
                   value={f.memorial_address} onChange={(e) => upd("memorial_address", e.target.value)}
                   className={inp} style={st} />
            <textarea rows={3} placeholder="Any directions or notes (which section, plot number, gate closest to it, etc.)"
                      value={f.memorial_notes} onChange={(e) => upd("memorial_notes", e.target.value)}
                      className={`${inp} md:col-span-2`} style={st} />
          </div>
        </section>

        {/* Timing */}
        <section className="wofy-card space-y-4">
          <div className="overline inline-flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5" style={{ color: "var(--wofy-primary)" }} />
            WHEN
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <input type="date" data-testid="ss-preferred-date"
                   value={f.preferred_date} onChange={(e) => upd("preferred_date", e.target.value)}
                   className={inp} style={st} />
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={f.flexible_dates}
                     onChange={(e) => upd("flexible_dates", e.target.checked)} />
              My dates are flexible
            </label>
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={f.recurring}
                     onChange={(e) => upd("recurring", e.target.checked)} />
              Recurring / annual
            </label>
            {f.recurring && (
              <input placeholder="e.g. every anniversary, every Diwali"
                     value={f.recurring_pattern} onChange={(e) => upd("recurring_pattern", e.target.value)}
                     className={inp} style={st} />
            )}
          </div>
        </section>

        {/* Photos */}
        <section className="wofy-card space-y-4">
          <div className="overline inline-flex items-center gap-1.5">
            <Camera className="w-3.5 h-3.5" /> PHOTOS
            <span className="opacity-60 font-normal normal-case tracking-normal ml-1">(optional)</span>
          </div>
          <input type="file" accept="image/*" onChange={onCover} data-testid="ss-cover" className="text-xs" />
          {cover && <img src={cover} className="mt-2 w-full max-w-md rounded-lg border" alt="cover" />}
          <input type="file" accept="image/*" onChange={onAdd} className="text-xs block" />
          {images.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {images.map((img, i) => (
                <div key={i} className="relative">
                  <img src={img} alt="" className="w-24 h-24 rounded-lg object-cover" />
                  <button type="button" onClick={() => setImages((p) => p.filter((_, j) => j !== i))}
                          className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-red-500 text-white grid place-items-center">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Contact */}
        <section className="wofy-card space-y-4">
          <div className="overline inline-flex items-center gap-1.5">
            <User className="w-3.5 h-3.5" /> HOW HELPERS SHOULD CONTACT YOU
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <input required placeholder="Your full name *" data-testid="ss-req-name"
                   value={f.requester_full_name} onChange={(e) => upd("requester_full_name", e.target.value)}
                   className={inp} style={st} />
            <input required type="email" placeholder="Email *" data-testid="ss-req-email"
                   value={f.requester_email} onChange={(e) => upd("requester_email", e.target.value)}
                   className={inp} style={st} />
            <input placeholder="Phone (optional)" data-testid="ss-req-phone"
                   value={f.requester_phone} onChange={(e) => upd("requester_phone", e.target.value)}
                   className={inp} style={st} />
            <select value={f.preferred_contact} onChange={(e) => upd("preferred_contact", e.target.value)}
                    data-testid="ss-preferred-contact" className={inp} style={st}>
              <option value="email">Prefer email</option>
              <option value="phone">Prefer phone</option>
              <option value="either">Either</option>
            </select>
            <input placeholder="Your country" value={f.requester_country}
                   onChange={(e) => upd("requester_country", e.target.value)} className={inp} style={st} />
            <input placeholder="Your city" value={f.requester_city}
                   onChange={(e) => upd("requester_city", e.target.value)} className={inp} style={st} />
          </div>
          <p className="text-[11px] opacity-60">
            Your contact details are visible only to signed-in members and admins —
            hidden from anonymous visitors and search engines.
          </p>
        </section>

        {/* Offering */}
        <section className="wofy-card space-y-4">
          <div className="overline">BUDGET &amp; OFFERING <span className="opacity-60 font-normal normal-case tracking-normal">(optional)</span></div>
          <div className="grid md:grid-cols-2 gap-4">
            <select value={f.budget_range} onChange={(e) => upd("budget_range", e.target.value)} className={inp} style={st}>
              <option value="">Budget range</option>
              <option value="Volunteer / no budget">Volunteer / no budget</option>
              <option value="Reimburse expenses only">Reimburse expenses only</option>
              <option value="$25–$50">$25–$50</option>
              <option value="$50–$100">$50–$100</option>
              <option value="$100–$250">$100–$250</option>
              <option value="$250+">$250+</option>
              <option value="Open to discussion">Open to discussion</option>
            </select>
            <input placeholder="Anything you're offering in return (e.g., 'will cover flowers + travel')"
                   value={f.offering} onChange={(e) => upd("offering", e.target.value)}
                   className={inp} style={st} />
          </div>
        </section>

        {/* Consent */}
        <section className="wofy-card">
          <label className="flex items-start gap-3 text-sm">
            <input type="checkbox" data-testid="ss-consent" required
                   checked={f.consent} onChange={(e) => upd("consent", e.target.checked)}
                   className="mt-1" />
            <span>
              The details are accurate to the best of my knowledge and I consent to
              publish this request on Social Pulse. I understand my contact details will
              be visible to signed-in members so they can reach out to help.
            </span>
          </label>
        </section>

        <div className="flex justify-end gap-3">
          <button type="button" onClick={() => nav("/services")} className="wofy-btn-ghost">Cancel</button>
          <button type="submit" disabled={submitting} data-testid="ss-submit" className="wofy-btn-primary">
            {submitting ? "Submitting…" : "Post request"}
          </button>
        </div>
      </form>
    </div>
  );
}
